// /analytika/reklamy — útrata a výsledky kampaní v Metě a Google Ads (22. 9. 2026).
// Meta bere všechny kampaně z účtu, ne jen [BRAND]. Kampaň se k produktu
// přiřadí podle názvu (netlify/functions/_lib/metaKampane.cjs).
import React, { useMemo, useState } from "react";
import { Megaphone, RefreshCw, List, CalendarDays, AlertTriangle, Target, Code2, Copy } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Dlazdice, ErrorNote, SkeletonCards, Zalozky, Stitek, EmptyState } from "../../components/ui.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, Pilulka, HlaskaDlazdice, AkceHlavicky,
} from "../../components/PrehledDlazdice.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { useApiData } from "../../lib/api.js";
import { toast } from "../../components/Toast.jsx";
import { useObdobi, VyberObdobi, useAnalytika, cislo, kc, vysledekText, datumText, tvar } from "./spolecne.jsx";

const NAZVY_PRODUKTU = {
  "bootcampy-kbely": "Bootcampy Kbely", "bootcampy-letna": "Bootcampy Letná", trenuj: "Trénuj se mnou", "dotaznik-reklama": "Dotazník z reklam", klub: "Klub FitWay40",
  jsz: "Jídelníček spokojené ženy", jidelnicek: "Jídelníček na míru", kviz: "Kvíz", ostatni: "Nezařazeno",
  bootcampy: "Bootcampy, lokalita nepoznaná", znacka: "Značka",
};
/** Produkt kampaně. Obecná kampaň na bootcampy se dělí podle sad reklam. */
const nazevProduktu = (k) => {
  const klice = Object.keys(k.rozpad || {}).filter((x) => (k.rozpad[x] || 0) > 0);
  if (klice.length > 1) return klice.map((x) => NAZVY_PRODUKTU[x] || x).join(" a ").replace("Bootcampy Kbely a Bootcampy Letná", "Bootcampy Kbely a Letná");
  return NAZVY_PRODUKTU[k.produkt] || k.produkt;
};
// Kampaně na prodej, které mají optimalizovat na nákup. Jen bootcampy, [BRAND]
// kampaně jsou značkové (video, dosah) a mají jiný cíl schválně.
const PRODEJNI = new Set(["bootcampy-kbely", "bootcampy-letna", "bootcampy"]);

const tonOptimalizace = (k) => {
  if (!k.optimalizace) return "neutralni";
  if (PRODEJNI.has(k.produkt)) return k.optimalizaceUdalost === "PURCHASE" ? "ok" : "pozor";
  return "znacka";
};

function Denne({ od, doD, meta, google }) {
  const dny = [];
  for (let d = od; d <= doD && dny.length < 62; ) {
    dny.push(d);
    const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + 1); d = x.toISOString().slice(0, 10);
  }
  const hodnoty = dny.map((d) => ({ d, m: Number(meta?.[d]) || 0, g: Number(google?.[d]) || 0 }));
  const max = Math.max(1, ...hodnoty.map((h) => h.m + h.g));
  const popisky = dny.length <= 10;
  return (
    <div className="px-4 pb-4 pt-3 sm:px-5">
      <div className="flex h-36 items-end gap-1 border-b border-slate-200">
        {hodnoty.map((h) => (
          <div key={h.d} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${datumText(h.d)}: Meta ${cislo(h.m)} Kč, Google ${cislo(h.g)} Kč`}>
            {h.g > 0 && <div className="w-full rounded-t-sm bg-sky-500" style={{ height: `${(h.g / max) * 100}%` }} />}
            <div className={`w-full bg-brand ${h.g > 0 ? "" : "rounded-t-sm"}`} style={{ height: `${(h.m / max) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between gap-1 text-xs text-slate-500">
        {popisky ? hodnoty.map((h) => <span key={h.d} className="flex-1 text-center">{new Date(`${h.d}T12:00:00Z`).getUTCDate()}.</span>)
          : <><span>{datumText(od)}</span><span>{datumText(doD)}</span></>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Pilulka><span aria-hidden className="h-2.5 w-2.5 rounded-sm bg-brand" />Meta</Pilulka>
        <Pilulka><span aria-hidden className="h-2.5 w-2.5 rounded-sm bg-sky-500" />Google Ads</Pilulka>
      </div>
    </div>
  );
}

function GoogleNapojeni() {
  const { data } = useApiData("/api/analytika-google-ads");
  const [ukazat, setUkazat] = useState(false);
  if (!data) return null;
  const kopiruj = async () => {
    try { await navigator.clipboard.writeText(data.skript || ""); toast("Skript zkopírován", "success"); }
    catch { toast("Kopírování se nepovedlo, označ text ručně", "error"); }
  };
  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Code2}
        nazev="Propojení Google Ads"
        odznak={data.propojeno ? "propojeno" : "čeká na skript"}
        odznakTon={data.propojeno ? "bily" : "zluty"}
        akce={data.skript && <AkceHlavicky ikona={Copy} popis="Zkopírovat skript" onClick={kopiruj} />}
      />
      <RadkyPrehledu>
        <RadekPrehledu popisek="Naposledy přišla data" hodnota={data.naposledy ? new Date(data.naposledy).toLocaleString("cs-CZ") : "zatím nikdy"} />
        <RadekPrehledu popisek="Uložené dny" hodnota={data.od ? `${datumText(String(data.od).slice(0, 10))} až ${datumText(String(data.do).slice(0, 10))}` : "—"} />
      </RadkyPrehledu>
      <div className="px-4 pb-4 sm:px-5">
        <p className="mt-2 text-sm text-slate-600">
          V Google Ads otevři Nástroje, Hromadné akce, Skripty. Založ nový skript, vlož text níže, povol přístup a nastav spouštění jednou denně.
        </p>
        <button type="button" className="btn-secondary btn-sm mt-3" onClick={() => setUkazat((v) => !v)}>
          {ukazat ? "Skrýt skript" : "Zobrazit skript"}
        </button>
        {ukazat && (
          <pre className="mt-3 max-h-80 overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-800">{data.skript}</pre>
        )}
      </div>
    </KartaPrehledu>
  );
}

/**
 * Co ukázat u kampaně Google. Google Ads konverze v účtu nastavené nemá, takže
 * když nic nevrátí, bereme výsledky z vlastního měření webu (29.9.2026, Láďa).
 */
function vysledekGoogle(k) {
  if (k.konverzeGoogle > 0) return `${cislo(k.konverzeGoogle)} ${tvar(k.konverzeGoogle, "konverze", "konverze", "konverzí")}`;
  if (k.konverzeWeb > 0) return `${vysledekText(k.konverzeWeb, k.typVysledku)} z webu`;
  return `0 ${k.typVysledku === "lead" ? "kontaktů" : "nákupů"}`;
}

export default function AnalytikaReklamy() {
  const obdobi = useObdobi();
  // Záložka platformy se pamatuje v prohlížeči (22. 9. 2026).
  const [platforma, nastavPlatformu] = useState(() => {
    try { return localStorage.getItem("fw40.analytika.platforma") || "vse"; } catch { return "vse"; }
  });
  const setPlatforma = (k) => {
    nastavPlatformu(k);
    try { localStorage.setItem("fw40.analytika.platforma", k); } catch { /* nevadí */ }
  };
  const { data, loading, error, refetch, refreshing } = useAnalytika(obdobi.rozsah);
  const uzka = useUzkaObrazovka();

  const kampane = useMemo(() => {
    if (!data) return [];
    const meta = (data.meta?.kampane || []).map((k) => ({ ...k, platforma: "Meta" }));
    const google = (data.google?.kampane || []).map((k) => ({
      id: `g-${k.kampan_id}`, nazev: k.kampan, platforma: "Google", produkt: k.produkt || null, utrata: k.utrata,
      kliky: k.kliky, cenaZaKlik: k.kliky ? Math.round(k.utrata / k.kliky) : null, nakupyMeta: null,
      konverzeGoogle: k.konverze, konverzeWeb: k.konverzeWeb || 0, typVysledku: k.typVysledku || "lead",
    }));
    return [...meta, ...google].filter((k) => platforma === "vse" || (platforma === "meta" ? k.platforma === "Meta" : k.platforma === "Google"));
  }, [data, platforma]);

  const s = data?.souhrn;
  const spatnaOptimalizace = (data?.meta?.kampane || []).filter((k) => k.stav === "ACTIVE" && PRODEJNI.has(k.produkt) && k.optimalizace && k.optimalizaceUdalost !== "PURCHASE");
  const bezProduktu = (data?.meta?.kampane || []).filter((k) => k.produkt === "ostatni" && k.utrata > 0);
  // [BRAND] kampaně nepatří k produktu, v přehledu po produktech mají vlastní řádek.
  const utrataZnacka = (data?.meta?.kampane || []).filter((k) => k.produkt === "znacka").reduce((x, k) => x + (k.utrata || 0), 0)
    + (data?.google?.kampane || []).filter((k) => k.produkt === "znacka").reduce((x, k) => x + (k.utrata || 0), 0);
  const metaNedostupna = !!s?.metaNedostupna;

  return (
    <Stranka>
      <GradientHeader
        icon={Megaphone}
        title="Reklamy"
        napoveda="Útrata a výsledky všech kampaní. Nákupy podle Mety hlásí Meta sama, skutečné nákupy jsou z našeho měření po produktech. Když se hodně rozcházejí, měření kulhá."
        loading={refreshing}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing }]}
      />
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="flex items-center">
          <Zalozky stitek="Platforma" aria-label="Platforma" value={platforma} onChange={setPlatforma}
            items={[{ key: "vse", label: "Vše" }, { key: "meta", label: "Meta" }, { key: "google", label: "Google Ads" }]} />
        </div>
        <VyberObdobi obdobi={obdobi} />
      </div>

      {error && <ErrorNote onRetry={refetch}>Data se nepodařilo načíst: {error}</ErrorNote>}
      {loading && !data && <SkeletonCards count={4} />}

      {s && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
            <Dlazdice popisek="Útrata celkem" hodnota={metaNedostupna ? "nedostupné" : kc(s.reklama)} ton="znacka" />
            <Dlazdice popisek="Meta" hodnota={metaNedostupna ? "nedostupné" : kc(s.reklamaMeta)} ton="znacka" doplnky={metaNedostupna && <Pilulka>Meta neodpověděla</Pilulka>} />
            <Dlazdice popisek="Google Ads" hodnota={data.google?.propojeno ? kc(s.reklamaGoogle) : "—"} ton="info" doplnky={!data.google?.propojeno && <Pilulka>čeká na propojení</Pilulka>} />
            <Dlazdice popisek="Skutečné nákupy" hodnota={cislo(s.nakupy)} ton="ok" doplnky={<Pilulka>{kc(s.trzba)}</Pilulka>} />
          </div>

          <KartaPrehledu>
            <HlavickaDlazdice ikona={List} nazev="Kampaně" odznak={String(kampane.length)} />
            {!kampane.length && <EmptyState icon={Megaphone} title="Žádná útrata" hint="Za vybrané období žádná kampaň neutrácela." />}
            {kampane.length > 0 && !uzka && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[56rem] text-sm tabular-nums">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-2.5">Kampaň</th>
                      <th className="px-3 py-2.5 text-right">Útrata</th>
                      <th className="px-3 py-2.5 text-right">Kliky na web</th>
                      <th className="px-3 py-2.5 text-right">Cena za klik</th>
                      <th className="px-3 py-2.5 text-right">Výsledky podle platformy</th>
                      <th className="px-5 py-2.5">Optimalizace</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {kampane.map((k) => (
                      <tr key={k.id}>
                        <td className="px-5 py-3">
                          <div className="font-bold text-slate-900">{k.nazev}</div>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            <Stitek ton={k.platforma === "Meta" ? "znacka" : "info"} velikost="mala">{k.platforma}</Stitek>
                            {k.produkt && <Stitek velikost="mala">{nazevProduktu(k)}</Stitek>}
                            {k.stav && k.stav !== "ACTIVE" && <Stitek velikost="mala">vypnutá</Stitek>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-bold">{kc(k.utrata)}</td>
                        <td className="px-3 py-3 text-right">{cislo(k.kliky)}</td>
                        <td className="px-3 py-3 text-right">{k.cenaZaKlik != null ? kc(k.cenaZaKlik) : "—"}</td>
                        <td className="px-3 py-3 text-right">{k.platforma === "Meta" ? `${vysledekText(k.nakupyMeta, "nakup")}, ${vysledekText(k.kontaktyMeta, "lead")}` : vysledekGoogle(k)}</td>
                        <td className="px-5 py-3">{k.optimalizace ? <Stitek ton={tonOptimalizace(k)} velikost="mala">{k.optimalizace}</Stitek> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {kampane.length > 0 && uzka && (
              <div className="space-y-2 p-3">
                {kampane.map((k) => (
                  <div key={k.id} className="polozka">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-base font-bold">{k.nazev}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <Stitek ton={k.platforma === "Meta" ? "znacka" : "info"} velikost="mala">{k.platforma}</Stitek>
                          {k.optimalizace && <Stitek ton={tonOptimalizace(k)} velikost="mala">{k.optimalizace}</Stitek>}
                          {k.stav && k.stav !== "ACTIVE" && <Stitek velikost="mala">vypnutá</Stitek>}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-base font-bold tabular-nums">{kc(k.utrata)}</div>
                        <div className="text-[13px] text-slate-500">útrata</div>
                      </div>
                    </div>
                    <div className="mt-2 text-[13px] text-slate-600">
                      {cislo(k.kliky)} {tvar(k.kliky, "klik", "kliky", "kliků")}, {k.platforma === "Meta" ? `${vysledekText(k.nakupyMeta, "nakup")}, ${vysledekText(k.kontaktyMeta, "lead")}` : vysledekGoogle(k)}
                      {k.produkt ? `, ${nazevProduktu(k)}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {spatnaOptimalizace.map((k) => (
              <HlaskaDlazdice key={k.id} ton="zluta" ikona={AlertTriangle} nadpis={`${k.nazev} optimalizuje na ${k.optimalizace}`}
                text="Kampaň na prodej má optimalizovat na Nákup. Po změně ji nech 2 až 3 dny bez zásahu." />
            ))}
            {bezProduktu.length > 0 && (
              <HlaskaDlazdice ton="zluta" ikona={AlertTriangle} nadpis="Kampaně bez produktu"
                text={`${bezProduktu.map((k) => k.nazev).join(", ")}: z názvu nejde poznat produkt. Pojmenuj je „BC Kbely…“, „BC Letná…“, „[BRAND]…“ nebo „[LEAD]…“.`} />
            )}
          </KartaPrehledu>

          <div className="grid gap-4 lg:grid-cols-2">
            <KartaPrehledu>
              <HlavickaDlazdice ikona={Target} nazev="Po produktech" podtitulek="Reklama proti skutečným výsledkům" />
              <RadkyPrehledu>
                {(data.produkty || []).filter((p) => p.reklama > 0 || p.vysledky > 0 || p.kontaktyMeta > 0).map((p) => (
                  <RadekPrehledu
                    key={p.klic}
                    popisek={p.nazev}
                    hodnota={metaNedostupna ? "nedostupné" : p.cenaZaVysledek != null ? kc(p.cenaZaVysledek) : p.reklama ? "bez výsledku" : "—"}
                    doplnek={`${metaNedostupna ? "útrata neznámá" : kc(p.reklama)}, ${vysledekText(p.vysledky + (p.kontaktyMeta || 0), p.typVysledku)}`}
                    tonHodnoty={!metaNedostupna && p.reklama && !p.vysledky && !p.kontaktyMeta ? "text-rose-700" : "text-slate-900"}
                  />
                ))}
                {utrataZnacka > 0 && !metaNedostupna && (
                  <RadekPrehledu popisek="Značka" hodnota={kc(utrataZnacka)} doplnek="povědomí o značce, nákup se nečeká" />
                )}
              </RadkyPrehledu>
            </KartaPrehledu>
            <KartaPrehledu>
              <HlavickaDlazdice ikona={CalendarDays} nazev="Útrata po dnech" />
              <Denne od={data.obdobi?.od || obdobi.rozsah.od} doD={data.obdobi?.do || obdobi.rozsah.do} meta={data.meta?.denne} google={data.google?.denne} />
            </KartaPrehledu>
          </div>

          <GoogleNapojeni />
        </>
      )}
    </Stranka>
  );
}
